import styled, { keyframes } from "styled-components";

import { isSmallDesktop, isSmallMobile, isTabletUp } from "@/hooks/layout";
import { colors, fonts, fontSizes, radius, shadows, transitions } from "@/styles/design-system";

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  align-items: center;
  text-align: center;
  justify-content: center;
  animation: ${slideUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  /* Framed card at ALL sizes now (phone included), scaled by breakpoint. */
  position: relative;
  width: 100%;
  max-width: 560px;
  background: ${colors.surface.card};
  border: 1px solid ${colors.border.light};
  padding: 32px 24px;
  border-radius: ${radius["2xl"]};
  box-shadow: ${shadows.xl};

  &::before {
    content: "";
    position: absolute;
    inset: -40px;
    border-radius: 60px;
    background: radial-gradient(circle, rgba(186, 144, 92, 0.12) 0%, transparent 70%);
    pointer-events: none;
    z-index: -1;
  }

  ${isTabletUp} {
    max-width: 1000px;
    padding: 48px 40px;
  }

  ${isSmallDesktop} {
    padding: 64px 56px;
  }
`;

export const HeroTitle = styled.h1`
  font-family: ${fonts.heading};
  font-size: 2.5rem;
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: 0.5px;
  color: ${colors.text.heading};
  margin: 0;
  max-width: 750px;
  text-align: center;

  span {
    color: ${colors.gold.accent};
  }

  ${isTabletUp} {
    font-size: 3rem;
  }

  ${isSmallDesktop} {
    font-size: 3.5rem;
  }

  ${isSmallMobile} {
    font-size: 1.9rem;
  }
`;

export const GoldDivider = styled.div`
  width: 200px;
  height: 1px;
  background: ${colors.gold.gradient};
`;

export const HeroDescription = styled.p`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 400;
  line-height: 1.6;
  color: ${colors.text.secondary};
  margin: 0;
  max-width: 500px;
  text-align: center;

  ${isSmallDesktop} {
    font-size: 1.0625rem;
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  width: 100%;
  margin-top: 4px;

  ${isTabletUp} {
    flex-direction: row;
    gap: 16px;
  }
`;

export const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 32px;
  background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
  border: none;
  border-radius: ${radius.md};
  color: ${colors.brown.darkest};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 500;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all ${transitions.normal};
  box-shadow: ${shadows.button.primary};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${shadows.button.primaryHover};
  }

  &:disabled {
    background: ${colors.brown.medium};
    color: ${colors.text.muted};
    box-shadow: none;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${colors.accent.blueIcon};
  }
`;

export const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 32px;
  background: rgba(235, 194, 142, 0.2);
  border: 1px solid ${colors.text.link};
  border-radius: ${radius.md};
  color: ${colors.text.primary};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 500;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all ${transitions.normal};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.glow.gold};
  }

  svg {
    width: 18px;
    height: 18px;
    color: #b9defb;
  }
`;

export const ProofBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-top: 28px;
  background: rgba(59, 45, 33, 0.7);
  border: 1px solid ${colors.border.light};
  backdrop-filter: blur(20px);
  border-radius: ${radius["3xl"]};
  padding: 0;
  overflow: hidden;
  width: 100%;
  max-width: 580px;
`;

export const ProofItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 14px 28px;
  position: relative;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0;
    top: 25%;
    height: 50%;
    width: 1px;
    background: ${colors.border.white};
  }
`;

export const ProofNumber = styled.span`
  font-family: ${fonts.number};
  font-size: ${fontSizes.body};
  font-weight: 700;
  color: #ffffff;
`;

export const ProofLabel = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.xs};
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
`;
